import { BellOff, BellRing, Lock, Send, Smartphone, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge, Button, cn, toast, toUiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { disablePush, enablePush, endpointHash, pushEnv, pushReadiness, usePwa } from '../mobile/pwa.ts';
import type { PushReadiness } from '../mobile/pwa.ts';
import { testSend, useNotifyState } from './api.ts';

function Row({ icon, title, children, tone }: { icon: ReactNode; title: string; children?: ReactNode; tone?: 'warn' | 'ok' }) {
  return (
    <div className="flex items-start gap-3">
      <span className={cn('mt-1 [&_svg]:size-4', tone === 'warn' ? 'text-waiting' : tone === 'ok' ? 'text-passed' : 'text-fg-subtle')}>{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-ui font-medium text-fg">{title}</p>
        {children && <div className="mt-1 text-ui text-fg-muted">{children}</div>}
      </div>
    </div>
  );
}

/** Push on this device: what is in the way, and the one button that clears it. */
export function PushControl({ compact }: { compact?: boolean }) {
  const notify = useNotifyState();
  const mode = useOrgus((s) => s.mode);
  // Re-render when the device state changes.
  usePwa((s) => `${s.endpoint}|${s.permission}|${s.standalone}|${!!s.registration}`);
  const env = pushEnv();
  const readiness: PushReadiness = pushReadiness(env);
  const endpoint = usePwa((s) => s.endpoint);
  const [busy, setBusy] = useState<'on' | 'off' | 'test' | null>(null);
  const [hash, setHash] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    if (endpoint) void endpointHash(endpoint).then((h) => live && setHash(h)).catch(() => undefined);
    else setHash(null);
    return () => {
      live = false;
    };
  }, [endpoint]);
  const channel = (hash && notify?.channels.find((c) => c.kind === 'push' && c.endpointHash === hash)) || null;

  if (!notify) {
    return (
      <Row icon={<BellOff />} title="Notifications are not running on this hub">
        Update the hub to a version with the notify module, then reload.
      </Row>
    );
  }
  if (mode === 'mock') {
    return (
      <Row icon={<BellOff />} title="Push is off in demo mode">
        Demo data never sends notifications. Connect to a hub to turn push on for this device.
      </Row>
    );
  }

  const run = async (what: 'on' | 'off' | 'test') => {
    setBusy(what);
    try {
      if (what === 'on') {
        if (!notify.vapidPublicKey) throw new Error('The hub has no push key yet.');
        await enablePush(notify.vapidPublicKey);
        toast.success('Notifications are on', 'Send a test to check this device.');
      } else if (what === 'off') {
        await disablePush();
        toast.success('Notifications are off for this device');
      } else {
        if (!channel) throw new Error('This device is not registered with the hub. Turn notifications off and on again.');
        const r = await testSend(channel.id);
        const failed = r.results.filter((x) => x.outcome === 'failed');
        if (failed.length) toast.error({ what: 'The test did not reach every channel.', why: failed[0]!.message ?? 'A push service refused it.', next: 'Turn notifications off and on again on that device.' });
        else toast.success('Test sent', 'It should arrive in a few seconds.');
      }
    } catch (e) {
      toast.error(toUiError(e, { what: what === 'on' ? 'Could not turn on notifications.' : 'That did not work.' }));
    } finally {
      setBusy(null);
    }
  };

  switch (readiness) {
    case 'insecure':
      return (
        <Row icon={<Lock />} title="Push needs a secure address" tone="warn">
          This page is open at {window.location.origin}, which is not https, so the browser offers no push here. Open Orgus at an https address such as
          Tailscale Serve; Phone access in Settings, Notifications shows how.
        </Row>
      );
    case 'install-first':
      return (
        <Row icon={<Smartphone />} title="Add Orgus to the Home Screen first" tone="warn">
          On iPhone and iPad, notifications only work for an installed app (iOS 16.4 or later). Install it, open it from the Home Screen, then turn notifications on there.
        </Row>
      );
    case 'unsupported':
      return (
        <Row icon={<TriangleAlert />} title="This browser cannot receive push" tone="warn">
          Use a current Chrome, Edge, Firefox or Safari. Every notification still shows in Recent alerts.
        </Row>
      );
    case 'denied':
      return (
        <Row icon={<BellOff />} title="Notifications are blocked" tone="warn">
          This browser was told not to allow them for this site. Allow notifications in the site settings, then reload.
        </Row>
      );
    case 'ready':
      return (
        <div className="flex flex-col gap-3">
          <Row icon={<BellOff />} title="Off on this device">
            Get approvals, waiting sessions, failures and budget alerts here, with Approve and Deny where the system supports buttons.
          </Row>
          <div className={compact ? 'flex flex-col' : 'flex'}>
            <Button variant="primary" size={compact ? 'lg' : 'md'} className={compact ? 'h-11' : undefined} icon={<BellRing />} loading={busy === 'on'} onClick={() => void run('on')}>
              Turn on notifications
            </Button>
          </div>
        </div>
      );
    case 'on':
      return (
        <div className="flex flex-col gap-3">
          <Row icon={<BellRing />} title="On for this device" tone="ok">
            {channel ? <Badge tone="passed">{channel.device}</Badge> : 'This device is subscribed.'}
          </Row>
          <div className="flex gap-2">
            <Button variant="secondary" size={compact ? 'lg' : 'md'} className={compact ? 'h-11 flex-1' : undefined} icon={<Send />} loading={busy === 'test'} onClick={() => void run('test')}>
              Send a test
            </Button>
            <Button variant="ghost" size={compact ? 'lg' : 'md'} className={compact ? 'h-11' : undefined} loading={busy === 'off'} onClick={() => void run('off')}>
              Turn off
            </Button>
          </div>
        </div>
      );
  }
}
